import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL from '../api';

// Theme Constants
const THEME = {
    primary: '#C0722C', // Gold/Copper
    secondary: '#2C1A10', // Dark Brown
    background: '#F8F6F2', // Cream
    sidebar: '#FFFFFF',
    text: '#333333'
};

const ICONS = {
    overview: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>,
    users: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    roles: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>,
    security: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>,
    audit: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>,
    master: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>,
    finance: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>,
    logout: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>,
    timer: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
};

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // Session Timer Logic with Absolute Persistence
    const [timeLeft, setTimeLeft] = useState(() => {
        const expiry = localStorage.getItem('sessionExpiry');
        if (expiry) {
            const remaining = Math.floor((parseInt(expiry, 10) - Date.now()) / 1000);
            return remaining > 0 ? remaining : 0;
        }
        // Initialize if not present
        const newExpiry = Date.now() + 1800 * 1000;
        localStorage.setItem('sessionExpiry', newExpiry.toString());
        return 1800;
    });

    const resetTimer = () => {
        const newExpiry = Date.now() + 1800 * 1000;
        localStorage.setItem('sessionExpiry', newExpiry.toString());
        return 1800; // Return numeric value to update state if needed
    };

    const [isCollapsed, setIsCollapsed] = useState(false);

    useEffect(() => {
        const timer = setInterval(() => {
            const expiry = localStorage.getItem('sessionExpiry');
            if (expiry) {
                const remaining = Math.floor((parseInt(expiry, 10) - Date.now()) / 1000);
                if (remaining <= 0) {
                    setTimeLeft(0);
                    clearInterval(timer);
                    handleLogout('Session Expired');
                } else {
                    setTimeLeft(remaining);
                }
            }
        }, 1000);

        return () => clearInterval(timer);
    }, []); // Empty dependency array for continuous isolated countdown

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const handleLogout = async (reason = 'Logged out') => {
        try {
            await axios.post(`${API_BASE_URL}/api/logout`, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
        } catch (e) { console.error('Logout failed', e); }

        localStorage.clear(); // Clears everything including token and timer
        // Alternatively, be specific if you want to keep some settings:
        // localStorage.removeItem('token');
        // localStorage.removeItem('sessionTimer');

        navigate('/');
        Swal.fire({
            title: 'Secure Logout',
            text: reason,
            icon: 'info',
            timer: 2000,
            showConfirmButton: false,
            background: THEME.background,
            color: THEME.text,
            confirmButtonColor: THEME.primary
        });
    };

    const navItems = [
        { label: 'Overview', path: '/admin/dashboard', icon: ICONS.overview },
        { label: 'User Directory', path: '/admin/users', icon: ICONS.users },
        { label: 'Role & Privileges', path: '/admin/roles', icon: ICONS.roles },
        { label: 'Audit Logs', path: '/admin/audit', icon: ICONS.audit },
        { label: 'Master Data', path: '/admin/master-data', icon: ICONS.master },
        { label: 'Committee Management', path: '/admin/committees', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg> },
        { label: 'Legislative Sessions', path: '/admin/sessions', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg> },
        { label: 'My Profile', path: '/admin/profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg> },
    ];

    return (
        <div style={{ display: 'flex', height: '100vh', backgroundColor: THEME.background, fontFamily: '"Outfit", sans-serif' }}>

            {/* Sidebar */}
            <div style={{
                width: isCollapsed ? '90px' : '280px',
                height: '100vh',
                backgroundColor: THEME.sidebar,
                borderRight: '1px solid #EAE2D5',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '4px 0 20px rgba(0,0,0,0.03)',
                zIndex: 10,
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative'
            }}>
                <div style={{ padding: isCollapsed ? '40px 15px' : '40px 30px', borderBottom: '1px solid #F0E8DB', transition: 'padding 0.3s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                        <div style={{
                            minWidth: '40px',
                            height: '40px',
                            backgroundColor: THEME.primary,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            boxShadow: '0 4px 10px rgba(192, 114, 44, 0.2)'
                        }}>
                            {ICONS.roles}
                        </div>
                        {!isCollapsed && (
                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                <h2 style={{ color: THEME.secondary, margin: 0, fontSize: '18px', fontWeight: '800', letterSpacing: '0.5px' }}>VIDHAN SABHA</h2>
                                <span style={{ fontSize: '11px', color: THEME.primary, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px' }}>Governor's Portal</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar Toggle Button */}
                <div
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    style={{
                        position: 'absolute',
                        right: '-12px',
                        top: '48px',
                        width: '24px',
                        height: '24px',
                        backgroundColor: THEME.sidebar,
                        border: '1px solid #EAE2D5',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                        zIndex: 11,
                        color: THEME.primary
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                </div>

                <nav style={{
                    flex: 1,
                    padding: isCollapsed ? '30px 15px' : '30px 20px',
                    transition: 'padding 0.3s',
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${THEME.primary}40 transparent`
                }}>
                    <style>
                        {`
                        nav::-webkit-scrollbar {
                            width: 5px;
                        }
                        nav::-webkit-scrollbar-track {
                            background: transparent;
                        }
                        nav::-webkit-scrollbar-thumb {
                            background: ${THEME.primary}40;
                            border-radius: 10px;
                        }
                        nav::-webkit-scrollbar-thumb:hover {
                            background: ${THEME.primary}60;
                        }
                        `}
                    </style>
                    {navItems.map((item) => (
                        <div
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            title={isCollapsed ? item.label : ''}
                            style={{
                                padding: '14px 20px',
                                margin: '8px 0',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: isCollapsed ? 'center' : 'flex-start',
                                gap: isCollapsed ? '0' : '15px',
                                backgroundColor: location.pathname === item.path ? '#FDF8F3' : 'transparent',
                                color: location.pathname === item.path ? THEME.primary : '#6B6B6B',
                                fontWeight: location.pathname === item.path ? '700' : '500',
                                border: location.pathname === item.path ? `1px solid ${THEME.primary}20` : '1px solid transparent',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                transform: (location.pathname === item.path && !isCollapsed) ? 'translateX(5px)' : 'none'
                            }}
                        >
                            <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: location.pathname === item.path ? THEME.primary : '#999',
                                transition: 'color 0.3s'
                            }}>
                                {item.icon}
                            </span>
                            {!isCollapsed && <span style={{ fontSize: '15px', whiteSpace: 'nowrap' }}>{item.label}</span>}
                        </div>
                    ))}
                </nav>

                <div style={{ padding: isCollapsed ? '30px 15px' : '20px', borderTop: '1px solid #F0E8DB', textAlign: 'center' }}>
                    <button
                        onClick={() => handleLogout()}
                        style={{
                            width: '100%',
                            padding: '14px',
                            backgroundColor: '#FFF5F5',
                            color: '#E53935',
                            border: '1.5px solid #FFEBEE',
                            borderRadius: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: isCollapsed ? '0' : '10px',
                            transition: 'all 0.2s',
                            fontSize: '14px'
                        }}
                    >
                        {ICONS.logout}
                        {!isCollapsed && <span>Secure Exit</span>}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Header */}
                <div style={{
                    height: '80px',
                    backgroundColor: '#FFFFFF',
                    borderBottom: '1px solid #F0E8DB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 50px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: THEME.secondary }}>Central Governance Console</h3>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>Bihar Vidhan Sabha • Secure Administrative Access</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '25px' }}>
                        <div style={{
                            padding: '10px 20px',
                            backgroundColor: '#2C1A10',
                            color: '#EAD7BB',
                            borderRadius: '30px',
                            fontSize: '13px',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            boxShadow: '0 4px 10px rgba(44, 26, 16, 0.2)'
                        }}>
                            <span style={{ display: 'flex', color: THEME.primary }}>{ICONS.timer}</span>
                            <span style={{ letterSpacing: '1px' }}>SESSION:</span>
                            <span style={{ fontFamily: '"Roboto Mono", monospace', fontSize: '15px', color: '#fff' }}>{formatTime(timeLeft)}</span>
                        </div>
                        <div
                            onClick={() => navigate('/admin/profile')}
                            style={{ display: 'flex', alignItems: 'center', gap: '12px', borderLeft: '1px solid #EEE', paddingLeft: '25px', cursor: 'pointer' }}
                        >
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '14px', fontWeight: '700', color: THEME.secondary }}>Governor Office</div>
                                <div style={{ fontSize: '11px', color: THEME.primary, fontWeight: '600', textTransform: 'uppercase' }}>Super Admin</div>
                            </div>
                            <div style={{
                                width: '45px',
                                height: '45px',
                                borderRadius: '14px',
                                backgroundColor: THEME.primary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontWeight: '800',
                                fontSize: '18px',
                                boxShadow: '0 4px 12px rgba(192, 114, 44, 0.3)'
                            }}>
                                G
                            </div>
                        </div>
                    </div>
                </div>

                {/* Page Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '40px' }}>
                    <Outlet />
                </div>
            </div>
        </div>
    );
};

export default AdminLayout;
